import React, { useState, useContext } from 'react';
import {
  Box,
  Flex,
  Stack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  Alert,
  AlertIcon,
  AlertDescription,
  useColorModeValue,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import AuthContext from '../context/AuthContext';
import { authService } from '../services/api';

function Login() {
  const { setToken, setUser } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBgColor = useColorModeValue('white', 'gray.700');
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    inviteCode: '',
  });
  
  const [formErrors, setFormErrors] = useState({
    username: '',
    password: '',
    inviteCode: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (!isLogin && formData.password.length < 12) {
      errors.password = 'Password must be at least 12 characters';
    }
    
    if (!isLogin && !formData.inviteCode) {
      errors.inviteCode = 'Invite code is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      let response;
      if (isLogin) {
        response = await authService.login(formData.username, formData.password);
      } else {
        response = await authService.register(formData.username, formData.password, formData.inviteCode);
      }
      setToken(response.token);
      setUser(response.user);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Authentication failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleView = () => {
    setIsLogin(!isLogin);
    setError('');
    setFormErrors({
      username: '',
      password: '',
      inviteCode: '',
    });
  };

  return (
    <Flex
      minH={'100vh'}
      align={'center'}
      justify={'center'}
      bg={bgColor}
    >
      <Stack spacing={8} mx={'auto'} maxW={'lg'} py={12} px={6}>
        <Stack align={'center'}>
          <Heading fontSize={'4xl'} textAlign={'center'} color="brand.500">
            RoleplAI
          </Heading>
          <Text fontSize={'lg'} color={'gray.600'}>
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </Text>
        </Stack>
        
        <Box
          rounded={'lg'}
          bg={cardBgColor}
          boxShadow={'lg'}
          p={8}
          w={{ base: 'full', md: '400px' }}
        >
          {error && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              <FormControl id="username" isInvalid={formErrors.username}>
                <FormLabel>Username</FormLabel>
                <Input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  autoComplete="username"
                />
                <FormErrorMessage>{formErrors.username}</FormErrorMessage>
              </FormControl>
              
              <FormControl id="password" isInvalid={formErrors.password}>
                <FormLabel>
                  Password 
                  {!isLogin && (
                    <Text as="span" fontSize="sm" color="gray.500" ml={1}>
                      (12+ chars, 3 of: uppercase, lowercase, numbers, symbols)
                    </Text>
                  )}
                </FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete={isLogin ? "current-password" : "new-password"}
                  />
                  <InputRightElement h={'full'}>
                    <Button
                      variant={'ghost'}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{formErrors.password}</FormErrorMessage>
              </FormControl>
              
              {!isLogin && (
                <FormControl id="inviteCode" isInvalid={formErrors.inviteCode}>
                  <FormLabel>Invite Code</FormLabel>
                  <Input
                    type="text"
                    name="inviteCode"
                    value={formData.inviteCode}
                    onChange={handleInputChange}
                  />
                  <FormErrorMessage>{formErrors.inviteCode}</FormErrorMessage>
                </FormControl>
              )}
              
              <Stack spacing={4} pt={2}>
                <Button
                  type="submit"
                  loadingText="Submitting"
                  size="lg"
                  colorScheme="brand"
                  isLoading={isLoading}
                >
                  {isLogin ? 'Sign in' : 'Sign up'}
                </Button>
                
                <Button
                  variant="outline"
                  colorScheme="brand"
                  onClick={toggleView}
                >
                  {isLogin ? 'Need to register?' : 'Already have account?'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Box>
      </Stack>
    </Flex>
  );
}

export default Login;